import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {TalentMatchStat} from '../../../types/B2B/B2BTypes';
import {safelyUseLanguageProfile} from '../../../utils/safelyUseLanguageProfile';

interface CompatibleTalentsSectionProps {
  talentMatchStats: TalentMatchStat[];
  cultureTypeImageUrl: string;
}

const formatCompatibilityScore = (averageCultureScore: number) => `${Math.round(averageCultureScore * 100)}%`;
const formatTotalTalents = (totalTalents: number) => (totalTalents > 0 ? `${totalTalents}+` : '0');

export const CompatibleTalentsSection = ({talentMatchStats, cultureTypeImageUrl}: CompatibleTalentsSectionProps) => {
  const {t, i18n} = useTranslation();
  const currentLanguage = i18n.resolvedLanguage as 'en' | 'de';
  const [firstTalent, secondTalent] = talentMatchStats;
  const totalTalents = talentMatchStats.reduce((sum, talent) => sum + talent.matchCount, 0);
  const firstTalentName = firstTalent
    ? safelyUseLanguageProfile(currentLanguage, {en: firstTalent.methodType.nameEn, de: firstTalent.methodType.nameDe})
    : '-';
  const secondTalentName = secondTalent
    ? safelyUseLanguageProfile(currentLanguage, {
      en: secondTalent.methodType.nameEn,
      de: secondTalent.methodType.nameDe,
    })
    : '-';

  return (
    <Grid
      width='100%'
      sx={{
        py: {xs: 6, lg: 9},
        px: {xs: 2, lg: 0},
        backgroundColor: '#ffffff',
      }}
    >
      <Typography
        sx={{
          color: '#000000',
          fontWeight: 700,
          fontSize: {xs: '2rem', sm: '2.5rem', lg: '3rem'},
          lineHeight: 1.15,
          textAlign: 'center',
        }}
      >
        {t('b2b.results.compatibleTalents.headline')}
      </Typography>

      <Box
        sx={{
          mt: {xs: 3, lg: 6},
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', lg: '402px 222px 402px'},
          alignItems: 'center',
          justifyContent: 'center',
          justifyItems: 'center',
          gap: {xs: 2.5, lg: 0},
        }}
      >
        <Box
          sx={{
            width: {xs: '100%', sm: 402},
            maxWidth: 402,
            height: {xs: 286, lg: 310},
            borderRadius: '5px',
            backgroundColor: 'rgba(254, 224, 184, 0.33)',
            boxShadow: '0px 3px 10px 0px rgba(0, 0, 0, 0.25)',
            p: '13px 13px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            justifySelf: {xs: 'stretch', sm: 'center', lg: 'end'},
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '5px',
              backgroundColor: '#fff',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: {xs: 132, lg: 168},
                height: {xs: 132, lg: 168},
                borderRadius: '50%',
                mx: 'auto',
                overflow: 'hidden',
                backgroundColor: cultureTypeImageUrl ? 'transparent' : '#c7dce2',
              }}
            >
              {cultureTypeImageUrl && (
                <Box
                  component='img'
                  src={cultureTypeImageUrl}
                  alt={String(t('b2b.results.compatibleTalents.companyLabel'))}
                  sx={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
                />
              )}
            </Box>
            <Typography
              sx={{
                mt: {xs: 1, lg: 1.5},
                color: '#163438',
                fontWeight: 700,
                fontSize: {xs: '1.125rem', lg: '1.25rem'},
                lineHeight: 1.5,
              }}
            >
              {t('b2b.results.compatibleTalents.companyLabel')}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            height: {xs: 110, lg: 132},
            width: {xs: '100%', lg: 222},
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            justifySelf: 'center',
          }}
        >
          <Box
            sx={{
              display: {xs: 'none', lg: 'block'},
              position: 'absolute',
              left: 0,
              top: 50,
              width: 100,
              height: '10px',
              backgroundColor: '#fee0b8',
            }}
          />
          <Box
            sx={{
              width: {xs: 100, lg: 132},
              height: {xs: 100, lg: 132},
              borderRadius: '50%',
              border: '4px solid #ffbf3e',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              zIndex: 9999,
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{color: '#163438', fontWeight: 700, fontSize: {xs: '1.25rem', lg: '1.5rem'}, lineHeight: 1.15}}
            >
              {t('b2b.results.compatibleTalents.matchLabel')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: {xs: 'none', lg: 'block'},
              position: 'absolute',
              right: 0,
              top: 82,
              width: 100,
              height: '10px',
              backgroundColor: '#fee0b8',
            }}
          />
        </Box>

        <Box
          sx={{
            width: {xs: '100%', sm: 402},
            maxWidth: 402,
            height: {xs: 300, lg: 310},
            borderRadius: '5px',
            backgroundColor: 'rgba(254, 224, 184, 0.33)',
            boxShadow: '0px 3px 10px 0px rgba(0, 0, 0, 0.25)',
            p: '13px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            overflow: 'hidden',
            justifySelf: {xs: 'stretch', sm: 'center', lg: 'start'},
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: {xs: 188, lg: 194},
              backgroundColor: '#fff',
              borderRadius: '5px',
              p: {xs: 1, lg: 1},
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: '#163438',
                textTransform: 'uppercase',
                textAlign: 'center',
                fontWeight: 500,
                fontSize: '0.625rem',
                lineHeight: 1.15,
                letterSpacing: '0.08em',
              }}
            >
              {t('b2b.results.compatibleTalents.talentPoolLabel')}
            </Typography>

            <Box sx={{display: 'flex', justifyContent: 'center', gap: {xs: 1, lg: 2.5}, mt: 1.1}}>
              <Box sx={{textAlign: 'center'}}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.625rem',
                    lineHeight: 1.15,
                    minHeight: 11,
                    width: {xs: 118, lg: 122},
                    color: '#000000',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {firstTalentName}
                </Typography>
                <Box
                  sx={{
                    width: 103,
                    height: 103,
                    borderRadius: '50%',
                    backgroundColor: firstTalent?.methodType.imageUrl ? 'transparent' : '#c7dce2',
                    mx: 'auto',
                    mt: 0.75,
                    overflow: 'hidden',
                  }}
                >
                  {firstTalent?.methodType.imageUrl && (
                    <Box
                      component='img'
                      src={firstTalent.methodType.imageUrl}
                      alt={firstTalentName}
                      sx={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    width: 55,
                    height: 24,
                    borderRadius: '13px',
                    backgroundColor: '#fff5e8',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.15, color: '#000000'}}>
                    {firstTalent ? formatCompatibilityScore(firstTalent.averageCultureScore) : '0%'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{textAlign: 'center'}}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.625rem',
                    lineHeight: 1.15,
                    minHeight: 11,
                    width: {xs: 118, lg: 122},
                    color: '#000000',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {secondTalentName}
                </Typography>
                <Box
                  sx={{
                    width: 103,
                    height: 103,
                    borderRadius: '50%',
                    backgroundColor: secondTalent?.methodType.imageUrl ? 'transparent' : '#c7dce2',
                    mx: 'auto',
                    mt: 0.75,
                    overflow: 'hidden',
                  }}
                >
                  {secondTalent?.methodType.imageUrl && (
                    <Box
                      component='img'
                      src={secondTalent.methodType.imageUrl}
                      alt={secondTalentName}
                      sx={{width: '100%', height: '100%', objectFit: 'cover', display: 'block'}}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    width: 55,
                    height: 24,
                    borderRadius: '13px',
                    backgroundColor: '#fff5e8',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.15, color: '#000000'}}>
                    {secondTalent ? formatCompatibilityScore(secondTalent.averageCultureScore) : '0%'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              fontSize: {xs: '1.75rem', lg: '1.5rem'},
              color: '#163438',
              mt: {xs: 1.25, lg: 1.5},
              lineHeight: 1.15,
              flexShrink: 0,
            }}
          >
            {formatTotalTalents(totalTalents)}
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 400,
              fontSize: {xs: '0.875rem', lg: '0.875rem'},
              color: '#163438',
              lineHeight: 1.15,
              px: {xs: 0.5, lg: 2},
              maxWidth: {xs: '100%', lg: 326},
              mx: 'auto',
              flexShrink: 0,
            }}
          >
            {t('b2b.results.compatibleTalents.description')}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};
